import { IconCalendar, IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useState, type ReactNode } from "react";
import { DayPicker, type Matcher } from "react-day-picker";
import { es } from "react-day-picker/locale";
import { cn } from "@/lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { TEXT_SIZE_CLASS } from "./tokens";

const HEIGHT_CLASS = { xs: "h-7", sm: "h-8", md: "h-9", lg: "h-10", xl: "h-12" } as const;

export interface DatePickerInputProps {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  placeholder?: string;
  /** Fecha mínima seleccionable (inclusive). */
  minDate?: Date;
  /** Fecha máxima seleccionable (inclusive). */
  maxDate?: Date;
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
  size?: keyof typeof HEIGHT_CLASS;
  className?: string;
  wrapperClassName?: string;
  /** Clases extra para el texto del placeholder (ej. aclararlo en filas de filtro). */
  placeholderClassName?: string;
}

/**
 * Reemplazo de `<DatePickerInput>` de Mantine (21 usos) ·
 * `react-day-picker` (calendario) + `Popover` propio (Radix) en vez del
 * date picker de Mantine. Formato de despliegue `DD/MM/YYYY` (convención
 * del portal, ver P43 en `CLAUDE.md`) vía `dayjs` — ya es dependencia del
 * repo, no hace falta `date-fns` para esto (react-day-picker lo trae para
 * SU cálculo interno de calendario, pero no lo exponemos acá).
 */
export function DatePickerInput({
  value,
  onChange,
  label,
  description,
  error,
  placeholder = "Selecciona una fecha",
  minDate,
  maxDate,
  disabled,
  required,
  clearable,
  size = "sm",
  className,
  wrapperClassName,
  placeholderClassName,
}: DatePickerInputProps) {
  const [open, setOpen] = useState(false);
  const disabledMatchers: Matcher[] = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  return (
    <div className={cn("flex flex-col gap-1", wrapperClassName)}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-peligro-600"> *</span>}
        </label>
      )}
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              // La fuente escala con `size` (igual que `Input` · antes estaba
              // fija en text-sm y un date picker size="xs" se veía más grande
              // que los inputs de texto vecinos).
              "flex w-full items-center justify-between gap-2 rounded-md border bg-background transition-[border-color,box-shadow] ease-out-quint duration-140",
              // Padding horizontal proporcional al tamaño (en xs los 12px de
              // px-3 se comían el espacio del placeholder en celdas angostas).
              size === "xs" ? "px-2" : "px-3",
              TEXT_SIZE_CLASS[size],
              "focus:outline-none focus:ring-2 focus:ring-multiva-600/30 focus:border-multiva-600",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              error ? "border-peligro-600" : "border-input",
              HEIGHT_CLASS[size],
              className
            )}
          >
            <span className={value ? "text-foreground" : cn("text-muted-foreground", placeholderClassName)}>
              {value ? dayjs(value).format("DD/MM/YYYY") : placeholder}
            </span>
            <span className="flex items-center gap-1 text-muted-foreground">
              {clearable && value && (
                <IconX
                  size={14}
                  role="button"
                  aria-label="Limpiar fecha"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                  className="hover:text-foreground"
                />
              )}
              <IconCalendar size={16} />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3">
          <DayPicker
            mode="single"
            locale={es}
            selected={value ?? undefined}
            onSelect={(date) => {
              onChange(date ?? null);
              setOpen(false);
            }}
            disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
            showOutsideDays
            components={{
              Chevron: ({ orientation }) =>
                orientation === "left" ? <IconChevronLeft size={16} /> : <IconChevronRight size={16} />,
            }}
            classNames={{
              months: "flex flex-col",
              month: "flex flex-col gap-3",
              month_caption: "flex items-center justify-center px-8 text-sm font-medium text-foreground",
              nav: "flex items-center justify-between absolute inset-x-0 top-0 px-1",
              button_previous:
                "flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30",
              button_next:
                "flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30",
              month_grid: "mt-1 border-collapse",
              weekdays: "flex",
              weekday: "size-8 text-center text-xs font-medium text-muted-foreground",
              week: "flex",
              day: "size-8 p-0 text-center text-sm",
              day_button:
                "flex size-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted",
              today: "font-semibold text-multiva-600",
              selected: "[&>button]:bg-multiva-600 [&>button]:text-white [&>button]:hover:bg-multiva-700",
              outside: "text-muted-foreground/40",
              disabled: "text-muted-foreground/30 pointer-events-none",
              hidden: "invisible",
            }}
          />
        </PopoverContent>
      </Popover>
      {error && typeof error !== "boolean" && <p className="text-xs text-peligro-600">{error}</p>}
    </div>
  );
}
